/**
 * The LOI review bar (docs/ux/loi.md): the state chip and sentence on the
 * left; the blocking sentence and Approve LOI on the right, the tab's only
 * ink primary once nothing blocks; refused, it stays focusable with the
 * sentence as its description. Sticky under the hub's compact strip. Carries
 * the approve and re-extract confirmations.
 */

import { cn } from 'cn';
import { FlagIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router';
import type { LoiHead } from '@/app/types';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { StateChip, type ChipTone } from '../casefile/ReviewBar';
import { blockingCount, blockingSentence, stateSentence, type Counts } from './describe';

export function chipFor(head: LoiHead, mine: boolean): { label: string; tone: ChipTone; icon?: boolean } {
  switch (head.state) {
    case 'approved': return { label: 'Approved', tone: 'done' };
    case 'unresolved': return { label: 'Unresolved', tone: 'warning', icon: true };
    case 'in_review': return { label: 'In review', tone: mine ? 'mine' : 'neutral' };
    default: return { label: 'Review required', tone: 'neutral' };
  }
}

export function LoiReviewBar({ matterRef, head, counts, mine, rerun, matterStatus, offline, approving, refused, onApprove, statusRef, extractionRunId, letterPages, recorded }: {
  matterRef: string;
  head: LoiHead;
  counts: Counts;
  mine: boolean;
  rerun: boolean;
  matterStatus: number;
  offline: boolean;
  approving: boolean;
  refused: { reasons: string[]; at: string } | null;
  onApprove: () => Promise<boolean>;
  statusRef?: React.Ref<HTMLSpanElement>;
  extractionRunId: string | null;
  letterPages: number | null;
  recorded: boolean;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dialogError, setDialogError] = useState(false);
  const inFlight = useRef(false);
  const approvedNow = useRef(false);
  const approved = head.state === 'approved';
  const blocking = blockingCount(counts);
  const canApprove = !approved && blocking === 0 && counts.total > 0;
  const chip = chipFor(head, mine);
  void letterPages; void recorded;

  async function confirm() {
    if (inFlight.current) return;
    inFlight.current = true;
    setDialogError(false);
    try {
      const ok = await onApprove();
      if (ok) { approvedNow.current = true; setConfirmOpen(false); return; }
      setDialogError(true);
    } finally {
      inFlight.current = false;
    }
  }

  return (
    <section aria-label="LOI review" data-slot="loi-review-bar" className="flex flex-col gap-2 rounded-xl border border-beam-line bg-beam-paper px-4 py-2">
      <div className="flex min-h-7 flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <StateChip label={chip.label} tone={chip.tone} icon={chip.icon ? <FlagIcon aria-hidden="true" className="size-3.5" /> : undefined} attrs={{ 'data-slot': 'review-state', 'data-review-state': head.state }} />
          <span className="flex flex-col text-[13px]">
            <span ref={statusRef} id="loi-status" tabIndex={-1} className="outline-none">{stateSentence(head, counts, mine, rerun)}</span>
            {!approved && matterStatus === 1 ? <span className="text-xs text-beam-text-muted">This approves the form, not the case. The case moves 1 to 2 from the header.</span> : null}
          </span>
        </div>
        <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-3">
          {approved ? (
            <Link to="?tab=audit" className="text-[13px] font-medium text-beam-signature underline-offset-4 hover:underline">Open the audit</Link>
          ) : (
            <>
              <span id="loi-blocking" className={cn('text-[13px]', canApprove ? 'text-beam-text-soft' : 'text-beam-text-muted')}>{offline ? `${blockingSentence(counts, head.authoritativeVersion)} Approval is paused while offline.` : blockingSentence(counts, head.authoritativeVersion)}</span>
              <Button
                variant={canApprove ? 'default' : 'outline'}
                size="lg"
                data-primary={canApprove && !offline ? 'true' : undefined}
                aria-disabled={canApprove && !approving ? undefined : 'true'}
                aria-describedby="loi-blocking"
                disabled={offline}
                className={cn('h-9', !canApprove && 'cursor-default text-beam-text-muted hover:bg-beam-paper')}
                onClick={() => { if (canApprove && !approving) { setDialogError(false); setConfirmOpen(true); } }}
              >
                {approving ? 'Approving' : 'Approve LOI'}
              </Button>
            </>
          )}
        </div>
      </div>
      {refused && !approved ? (
        <Alert role="alert" className="border-beam-warning-bg bg-beam-warning-bg text-beam-warning">
          <AlertTitle>Approval was refused at {refused.at}: {refused.reasons.join('; ')}.</AlertTitle>
          <AlertDescription className="text-beam-warning">Fix the items named and try again.</AlertDescription>
        </Alert>
      ) : null}

      <AlertDialog open={confirmOpen} onOpenChange={(o) => { if (!approving) setConfirmOpen(o); }}>
        <AlertDialogContent
          className="sm:max-w-[560px]"
          // After an approval the trigger is gone (the bar shows the audit link), so the dialog's focus return would land on the body; the status sentence takes it instead.
          onCloseAutoFocus={(e) => { if (approvedNow.current) { e.preventDefault(); approvedNow.current = false; document.getElementById('loi-status')?.focus(); } }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Approve the LOI?</AlertDialogTitle>
            <AlertDialogDescription>
              This writes the authoritative instruction form for {matterRef}, version {head.authoritativeVersion + 1}, from the {counts.decided} {counts.decided === 1 ? 'decision' : 'decisions'} made: {counts.accepted} accepted, {counts.corrected} corrected, {counts.missing} marked missing{counts.missing > 0 ? ` (${counts.missing === 1 ? 'that field' : 'those fields'} will be empty in the form)` : ''}.{counts.optionalUndecided > 0 ? ` ${counts.optionalUndecided} optional ${counts.optionalUndecided === 1 ? 'field is' : 'fields are'} undecided and will be empty in the form.` : ''} It sets the cycle's LOI approved gate; the case's status does not change. Decisions lock now and cannot be changed after approval. The extraction file{extractionRunId ? ` (run ${extractionRunId.slice(0, 8)})` : ''} is kept as it is.{head.authoritativeVersion > 0 ? ` Version ${head.authoritativeVersion} stays on file.` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {refused ? (
            <div role="alert" className="rounded-lg border border-l-[3px] border-beam-line border-l-beam-warning bg-beam-paper px-3 py-2 text-[13px]">
              <p className="font-medium">Approval was refused:</p>
              <ul className="mt-1 list-disc pl-5">{refused.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
            </div>
          ) : dialogError ? (
            <div role="alert" className="rounded-lg border border-l-[3px] border-beam-line border-l-beam-error bg-beam-paper px-3 py-2 text-[13px]">The LOI could not be approved. Try again.</div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel autoFocus disabled={approving}>Keep reviewing</AlertDialogCancel>
            <AlertDialogAction aria-disabled={approving ? 'true' : undefined} onClick={(e) => { e.preventDefault(); void confirm(); }}>{approving ? 'Approving' : 'Approve LOI'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

export function ReextractDialog({ open, onOpenChange, onConfirm, decided, runId, pages, recorded, busy, approvedVersion }: { open: boolean; onOpenChange: (o: boolean) => void; onConfirm: () => void; decided: number; runId: string | null; pages: number; recorded: boolean; busy: boolean; approvedVersion: number | null }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-[560px]">
        <AlertDialogHeader>
          <AlertDialogTitle>Run the extraction again?</AlertDialogTitle>
          <AlertDialogDescription>
            A new extraction reads the letter ({pages} {pages === 1 ? 'page' : 'pages'}) again and replaces the machine values under review. Every extraction file is kept{runId ? `, including run ${runId.slice(0, 8)}` : ''}. The {decided} {decided === 1 ? 'decision' : 'decisions'} made against that run {decided === 1 ? 'is' : 'are'} kept beside the new values as previous decisions and {decided === 1 ? 'is' : 'are'} not applied; the review returns to Review required.{approvedVersion ? ` The approved form, version ${approvedVersion}, stays on file; approving again writes version ${approvedVersion + 1}.` : ''}{recorded ? ' This environment serves the recorded result (LOI: recorded), so the new run reads the same values.' : ''}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel autoFocus>Keep this extraction</AlertDialogCancel>
          <AlertDialogAction aria-disabled={busy ? 'true' : undefined} onClick={(e) => { e.preventDefault(); if (!busy) onConfirm(); }}>Run extraction again</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
